// Settings — profile + (when system_admin) Statute Corpus + Admin Management.
// Phase 9 items 13 + 14.

import { useEffect, useMemo, useState } from "react";

import { admins, corpus } from "../lib/api";
import { EhrConnectCard } from "../components/EhrConnectCard";
import { useAuthStore } from "../lib/store";
import type {
  ApplyDecision,
  CorpusDiffPayload,
  CorpusStateResponse,
  CorpusUploadResponse,
  JurisdictionEntry,
  SystemAdminEntry,
} from "../lib/types";

// Per-row decision the operator made during review. Keyed by
// "<source_table>:<id>". Missing entry == approve (default).
type DecisionMap = Record<
  string,
  { action: "deny" | "modify"; text?: string; title?: string }
>;

function decisionKey(source_table: string, id: string): string {
  return `${source_table}:${id}`;
}

export function SettingsPage(): JSX.Element {
  const email = useAuthStore((s) => s.email);
  const role = useAuthStore((s) => s.role);
  const tenantId = useAuthStore((s) => s.tenantId);
  const isSystemAdmin = role === "system_admin";

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-slate-900">Settings</h1>

      <section className="card p-6">
        <h2 className="mb-3 text-lg font-medium text-slate-800">Profile</h2>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          <div>
            <dt className="font-medium text-slate-500">Email</dt>
            <dd className="text-slate-900">{email ?? "—"}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Role</dt>
            <dd className="text-slate-900">{role ?? "—"}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Tenant</dt>
            <dd className="text-slate-900">#{tenantId ?? "—"}</dd>
          </div>
          <div>
            <dt className="font-medium text-slate-500">Subject</dt>
            <dd className="text-slate-900">Account Holder (single-Subject MVP)</dd>
          </div>
        </dl>
      </section>

      <EhrConnectCard />

      {isSystemAdmin && (
        <>
          <StatuteCorpusCard />
          <AdminManagementCard />
        </>
      )}

      {!isSystemAdmin && (
        <section className="card p-6">
          <h2 className="mb-2 text-lg font-medium text-slate-800">About this MVP</h2>
          <ul className="list-disc space-y-1 pl-5 text-sm text-slate-700">
            <li>The Vault stores originals encrypted under per-tenant KMS keys.</li>
            <li>Statute Corpus + Admin Management sections appear when you sign in as a system_admin.</li>
          </ul>
        </section>
      )}
    </div>
  );
}

// ----- Statute Corpus card (system_admin only, Phase 9 upload flow) ---------
//
// Operator flow: pick state + source kind → choose PDF/TXT/DOCX → click
// Upload. Backend parses + diffs + caches a pending_diff. Card shows the
// preview (added / changed / removed counts + a section-level expander).
// Operator clicks Approve & Apply → atomic commit.

function StatuteCorpusCard(): JSX.Element {
  const [state, setState] = useState<CorpusStateResponse | null>(null);
  const [jurisdictions, setJurisdictions] = useState<JurisdictionEntry[]>([]);
  const [jurisdiction, setJurisdiction] = useState<string>("TX");
  const [sourceKind, setSourceKind] = useState<string>("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [applyElapsedMs, setApplyElapsedMs] = useState(0);
  const [uploadResult, setUploadResult] = useState<CorpusUploadResponse | null>(null);
  const [applyResult, setApplyResult] = useState<{
    version: string;
    added: number;
    changed: number;
    removed: number;
    appliedAt: number;
    embedSectionCount: number;
  } | null>(null);
  const [diff, setDiff] = useState<CorpusDiffPayload | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [decisions, setDecisions] = useState<DecisionMap>({});

  // Elapsed-time counter that ticks while applying so the operator can
  // see the Apply isn't frozen.
  useEffect(() => {
    if (!applying) {
      setApplyElapsedMs(0);
      return;
    }
    const start = Date.now();
    const interval = window.setInterval(() => {
      setApplyElapsedMs(Date.now() - start);
    }, 100);
    return () => window.clearInterval(interval);
  }, [applying]);

  const refresh = async () => {
    try {
      const s = await corpus.state();
      setState(s);
      if (s.has_pending_diff) {
        const d = await corpus.diff();
        setDiff(d.diff);
      } else {
        setDiff(null);
        setDecisions({});
      }
    } catch (e) {
      setError((e as Error).message);
    }
  };

  useEffect(() => {
    void refresh();
    corpus
      .jurisdictions()
      .then((r) => {
        setJurisdictions(r.jurisdictions);
        // Default source_kind = first enabled jurisdiction's first source.
        const first = r.jurisdictions.find((j) => j.enabled);
        if (first && first.sources.length) setSourceKind(first.sources[0].id);
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  const activeJurisdiction = useMemo(
    () => jurisdictions.find((j) => j.code === jurisdiction) ?? null,
    [jurisdictions, jurisdiction],
  );

  const onUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !jurisdiction || !sourceKind) return;
    setUploading(true);
    setError(null);
    setUploadResult(null);
    setDecisions({});
    try {
      const resp = await corpus.upload(file, jurisdiction, sourceKind);
      setUploadResult(resp);
      await refresh();
      setShowPreview(true);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setUploading(false);
    }
  };

  // Effective counts (after operator deny decisions are applied client-side).
  const effective = useMemo(() => {
    if (!diff) return { approved: 0, denied: 0, modified: 0, totalRows: 0 };
    let approved = 0;
    let denied = 0;
    let modified = 0;
    const total = diff.added.length + diff.changed.length + diff.removed.length;
    const eachRow = [
      ...diff.added.map((d) => ({ source_table: d.source_table, id: d.id })),
      ...diff.changed.map((d) => ({ source_table: d.source_table, id: d.id })),
      ...diff.removed.map((d) => ({ source_table: d.source_table, id: d.id })),
    ];
    for (const r of eachRow) {
      const d = decisions[decisionKey(r.source_table, r.id)];
      if (!d) approved += 1;
      else if (d.action === "deny") denied += 1;
      else if (d.action === "modify") modified += 1;
    }
    return { approved: approved + modified, denied, modified, totalRows: total };
  }, [diff, decisions]);

  const onApply = async () => {
    if (!diff) return;
    if (effective.approved === 0) {
      setError("All rows denied. Nothing to apply.");
      return;
    }
    if (
      !confirm(
        `Apply ${effective.approved} approved row${
          effective.approved === 1 ? "" : "s"
        } to the live corpus?\n` +
          `(denied: ${effective.denied}, modified: ${effective.modified})`,
      )
    )
      return;
    const embedSectionCount =
      diff.added.length +
      diff.changed.length -
      Object.entries(decisions).filter(
        ([_, d]) => d.action === "deny",
      ).length;
    setApplying(true);
    setError(null);
    setApplyResult(null);
    try {
      const decisionsArray: ApplyDecision[] = Object.entries(decisions).map(
        ([k, d]) => {
          const [source_table, id] = k.split(":", 2);
          return {
            source_table,
            id,
            action: d.action,
            ...(d.text !== undefined ? { text: d.text } : {}),
            ...(d.title !== undefined ? { title: d.title } : {}),
          };
        },
      );
      const resp = await corpus.apply(decisionsArray);
      setApplyResult({
        version: resp.version,
        added: resp.added,
        changed: resp.changed,
        removed: resp.removed,
        appliedAt: Date.now(),
        embedSectionCount,
      });
      setFile(null);
      setUploadResult(null);
      setShowPreview(false);
      setDecisions({});
      await refresh();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setApplying(false);
    }
  };

  const setRowDecision = (
    source_table: string,
    id: string,
    next: { action: "deny" | "modify"; text?: string; title?: string } | null,
  ): void => {
    setDecisions((prev) => {
      const updated = { ...prev };
      const k = decisionKey(source_table, id);
      if (next === null) delete updated[k];
      else updated[k] = next;
      return updated;
    });
  };

  // Post-apply embedding countdown. Coarse estimate (~500ms per
  // section + 2s overhead) since the worker doesn't yet emit per-
  // section progress events. The interval below ticks the value down
  // so the UI shows a live "rebuilding semantic index" indicator that
  // resolves itself.
  const [embedSecondsLeft, setEmbedSecondsLeft] = useState(0);
  useEffect(() => {
    if (!applyResult) {
      setEmbedSecondsLeft(0);
      return;
    }
    const ESTIMATE_MS_PER_SECTION = 500;
    const totalMs = applyResult.embedSectionCount * ESTIMATE_MS_PER_SECTION + 2000;
    const tick = () => {
      const left = Math.max(
        0,
        Math.ceil((totalMs - (Date.now() - applyResult.appliedAt)) / 1000),
      );
      setEmbedSecondsLeft(left);
      return left;
    };
    tick();
    const interval = window.setInterval(() => {
      if (tick() === 0) {
        window.clearInterval(interval);
      }
    }, 500);
    return () => window.clearInterval(interval);
  }, [applyResult]);

  if (!state) {
    return (
      <section className="card p-6">
        <h2 className="text-lg font-medium text-slate-800">Statute Corpus</h2>
        <p className="mt-2 text-sm text-slate-500">Loading…</p>
      </section>
    );
  }

  const totalSections =
    state.section_counts.tx_labor_code_sections +
    state.section_counts.tx_administrative_code_sections +
    state.section_counts.tx_insurance_code_sections;

  const staleDays = activeJurisdiction?.stale_days ?? 365;
  const daysSinceRefresh = state.last_refreshed_at
    ? Math.floor(
        (Date.now() - new Date(state.last_refreshed_at).getTime()) /
          (1000 * 60 * 60 * 24),
      )
    : null;
  const isStale = daysSinceRefresh !== null && daysSinceRefresh > staleDays;

  return (
    <section className="card p-6">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-lg font-medium text-slate-800">Statute Corpus</h2>
        <StatusBadge status={state.status} />
      </div>

      <p className="mt-1 text-xs text-slate-500">
        The corpus is operator-uploaded. CaseLens does not maintain authoritative
        statute content — you upload the canonical publication for your
        jurisdiction, the system stores it, and every draft is pinned to the
        version you uploaded.
      </p>

      <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
        <div>
          <dt className="font-medium text-slate-500">Current version</dt>
          <dd className="text-slate-900">{state.current_version ?? "—"}</dd>
        </div>
        <div>
          <dt className="font-medium text-slate-500">Last upload applied</dt>
          <dd className={isStale ? "text-amber-700" : "text-slate-900"}>
            {formatTs(state.last_refreshed_at)}
            {daysSinceRefresh !== null && (
              <span className="ml-1 text-xs text-slate-500">
                ({daysSinceRefresh}d ago{isStale && ` — over ${staleDays}d threshold`})
              </span>
            )}
          </dd>
        </div>
        <div>
          <dt className="font-medium text-slate-500">Total sections stored</dt>
          <dd className="text-slate-900">{totalSections}</dd>
        </div>
        <div>
          <dt className="font-medium text-slate-500">Per-source counts</dt>
          <dd className="text-slate-900">
            <ul className="text-xs">
              <li>Tex. Lab. Code: {state.section_counts.tx_labor_code_sections}</li>
              <li>28 TAC: {state.section_counts.tx_administrative_code_sections}</li>
              <li>Tex. Ins. Code: {state.section_counts.tx_insurance_code_sections}</li>
            </ul>
          </dd>
        </div>
      </dl>

      {state.error_message && (
        <p className="mt-2 text-sm text-red-600">Error: {state.error_message}</p>
      )}

      <form onSubmit={onUpload} className="mt-5 space-y-3 border-t border-slate-200 pt-4">
        <h3 className="text-sm font-medium text-slate-700">Upload a new corpus document</h3>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600">State</label>
            <select
              className="input mt-1"
              value={jurisdiction}
              onChange={(e) => {
                setJurisdiction(e.target.value);
                const j = jurisdictions.find((x) => x.code === e.target.value);
                setSourceKind(j && j.sources.length ? j.sources[0].id : "");
              }}
            >
              {jurisdictions.map((j) => (
                <option key={j.code} value={j.code} disabled={!j.enabled}>
                  {j.label}
                  {!j.enabled ? " (coming soon)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-slate-600">Source</label>
            <select
              className="input mt-1 w-full"
              value={sourceKind}
              onChange={(e) => setSourceKind(e.target.value)}
              disabled={!activeJurisdiction?.enabled || !activeJurisdiction.sources.length}
            >
              {(activeJurisdiction?.sources ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs font-medium text-slate-600">File (PDF / TXT / DOCX)</label>
            <input
              type="file"
              accept=".pdf,.txt,.docx,application/pdf,text/plain,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="mt-1 block w-full text-sm text-slate-700"
            />
          </div>
          <button
            type="submit"
            className="btn-primary"
            disabled={uploading || !file || !sourceKind || !activeJurisdiction?.enabled}
          >
            {uploading ? "Uploading…" : "Upload + Preview"}
          </button>
        </div>
        {uploadResult && (
          <p className="text-sm text-slate-700">
            Parsed {uploadResult.parsed_sections} sections — added{" "}
            <strong>{uploadResult.added}</strong>, changed{" "}
            <strong>{uploadResult.changed}</strong>, removed{" "}
            <strong>{uploadResult.removed}</strong>.
          </p>
        )}
      </form>

      {diff && (
        <div className="mt-4 border-t border-slate-200 pt-4">
          <div className="mb-2 flex items-center justify-between gap-2">
            <h3 className="text-sm font-medium text-slate-700">Pending change preview</h3>
            <button
              type="button"
              className="text-xs text-brand-700 hover:underline"
              onClick={() => setShowPreview((s) => !s)}
            >
              {showPreview ? "Hide details" : "Show per-section details"}
            </button>
          </div>
          <ul className="text-sm text-slate-700">
            <li>
              <span className="badge-green">added</span> {diff.added.length} section
              {diff.added.length === 1 ? "" : "s"}
            </li>
            <li>
              <span className="badge-yellow">changed</span> {diff.changed.length}
            </li>
            <li>
              <span className="badge-red">removed</span> {diff.removed.length}
            </li>
          </ul>
          {(effective.denied > 0 || effective.modified > 0) && (
            <p className="mt-2 text-xs text-slate-600">
              Operator review so far: <strong>{effective.approved}</strong> approved
              {effective.modified > 0 && (
                <>
                  {" "}
                  (incl. <strong>{effective.modified}</strong> edited)
                </>
              )}
              , <strong>{effective.denied}</strong> denied, of{" "}
              {effective.totalRows} total.
            </p>
          )}
          {showPreview && (
            <DiffSectionList
              diff={diff}
              decisions={decisions}
              setRowDecision={setRowDecision}
            />
          )}
          <div className="mt-3 flex flex-wrap items-center gap-3">
            <button
              type="button"
              className="btn-primary"
              onClick={onApply}
              disabled={applying}
            >
              {applying ? (
                <span className="flex items-center gap-2">
                  <span
                    className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white/40 border-t-white"
                    aria-hidden
                  />
                  Applying… {(applyElapsedMs / 1000).toFixed(1)}s
                </span>
              ) : effective.approved === 0 ? (
                "All rows denied"
              ) : (
                `Approve & Apply (${effective.approved})`
              )}
            </button>
            {applying ? (
              <span className="text-xs text-slate-600">
                Writing sections + bumping version. Embedding runs in the
                background after this returns.
              </span>
            ) : (
              <span className="text-xs text-slate-500">
                Apply is atomic — advisory lock, version bump, audit row.
              </span>
            )}
            {applying && applyElapsedMs > 15000 && (
              <span className="text-xs text-amber-700">
                Taking longer than usual — large uploads can hit ~30s.
              </span>
            )}
          </div>
        </div>
      )}

      {applyResult && (
        <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-emerald-900">
                Applied corpus v{applyResult.version}
              </h3>
              <p className="mt-1 text-xs text-emerald-800">
                {applyResult.added} added · {applyResult.changed} changed ·{" "}
                {applyResult.removed} removed. Citation Verifier already
                resolves the new sections; keyword search works immediately.
              </p>
              {applyResult.embedSectionCount > 0 && embedSecondsLeft > 0 && (
                <p className="mt-2 flex items-center gap-2 text-xs text-emerald-700">
                  <span
                    className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-emerald-300 border-t-emerald-700"
                    aria-hidden
                  />
                  Semantic search index rebuilding (~{embedSecondsLeft}s left for{" "}
                  {applyResult.embedSectionCount} section
                  {applyResult.embedSectionCount === 1 ? "" : "s"}).
                </p>
              )}
              {applyResult.embedSectionCount > 0 && embedSecondsLeft === 0 && (
                <p className="mt-2 text-xs text-emerald-700">
                  Semantic search index updated.
                </p>
              )}
            </div>
            <button
              type="button"
              className="text-xs text-emerald-700 hover:underline"
              onClick={() => setApplyResult(null)}
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {error && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}

// Pretty source labels used in the preview headers.
const SOURCE_LABELS: Record<string, string> = {
  tx_labor_code_sections: "Texas Labor Code",
  tx_administrative_code_sections: "28 TAC (DWC rules)",
  tx_insurance_code_sections: "Texas Insurance Code Ch. 1305",
};

interface DiffRow {
  kind: "added" | "changed" | "removed";
  source_table: string;
  id: string;
  citation: string;
  title: string;
  text: string;
}

function DiffSectionList({
  diff,
  decisions,
  setRowDecision,
}: {
  diff: CorpusDiffPayload;
  decisions: DecisionMap;
  setRowDecision: (
    source_table: string,
    id: string,
    next: { action: "deny" | "modify"; text?: string; title?: string } | null,
  ) => void;
}): JSX.Element {
  // Flatten + group by source. Use the section payload when present
  // (added / changed have it; removed only carries the id).
  const rows: DiffRow[] = [];
  for (const d of diff.added) {
    rows.push({
      kind: "added",
      source_table: d.source_table,
      id: d.id,
      citation: d.section?.citation ?? d.id,
      title: d.section?.title ?? "",
      text: d.section?.text ?? "",
    });
  }
  for (const d of diff.changed) {
    rows.push({
      kind: "changed",
      source_table: d.source_table,
      id: d.id,
      citation: d.section?.citation ?? d.id,
      title: d.section?.title ?? "",
      text: d.section?.text ?? "",
    });
  }
  for (const d of diff.removed) {
    rows.push({
      kind: "removed",
      source_table: d.source_table,
      id: d.id,
      citation: d.id,
      title: "",
      text: "",
    });
  }
  const grouped: Record<string, DiffRow[]> = {};
  for (const r of rows) {
    grouped[r.source_table] = grouped[r.source_table] ?? [];
    grouped[r.source_table].push(r);
  }

  if (rows.length === 0) {
    return (
      <p className="mt-2 text-sm text-slate-500">No sections to preview.</p>
    );
  }

  return (
    <div className="mt-3 space-y-4">
      {Object.entries(grouped).map(([source, items]) => (
        <SourceGroup
          key={source}
          source={source}
          items={items}
          decisions={decisions}
          setRowDecision={setRowDecision}
        />
      ))}
    </div>
  );
}

function SourceGroup({
  source,
  items,
  decisions,
  setRowDecision,
}: {
  source: string;
  items: DiffRow[];
  decisions: DecisionMap;
  setRowDecision: (
    source_table: string,
    id: string,
    next: { action: "deny" | "modify"; text?: string; title?: string } | null,
  ) => void;
}): JSX.Element {
  const added = items.filter((r) => r.kind === "added");
  const changed = items.filter((r) => r.kind === "changed");
  const removed = items.filter((r) => r.kind === "removed");

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50">
      <div className="flex items-center justify-between gap-3 border-b border-slate-200 bg-white px-4 py-2">
        <h4 className="text-sm font-semibold text-slate-900">
          {SOURCE_LABELS[source] ?? source}
        </h4>
        <div className="flex gap-2 text-xs">
          {added.length > 0 && (
            <span className="badge-green">+{added.length} added</span>
          )}
          {changed.length > 0 && (
            <span className="badge-yellow">~{changed.length} changed</span>
          )}
          {removed.length > 0 && (
            <span className="badge-red">−{removed.length} removed</span>
          )}
        </div>
      </div>
      <div className="max-h-[28rem] overflow-y-auto px-3 py-2 text-sm">
        {added.length > 0 && (
          <DiffKindBlock
            label="Added"
            kind="added"
            items={added}
            decisions={decisions}
            setRowDecision={setRowDecision}
          />
        )}
        {changed.length > 0 && (
          <DiffKindBlock
            label="Changed"
            kind="changed"
            items={changed}
            decisions={decisions}
            setRowDecision={setRowDecision}
          />
        )}
        {removed.length > 0 && (
          <DiffKindBlock
            label="Removed"
            kind="removed"
            items={removed}
            decisions={decisions}
            setRowDecision={setRowDecision}
          />
        )}
      </div>
    </div>
  );
}

function DiffKindBlock({
  label,
  kind,
  items,
  decisions,
  setRowDecision,
}: {
  label: string;
  kind: "added" | "changed" | "removed";
  items: DiffRow[];
  decisions: DecisionMap;
  setRowDecision: (
    source_table: string,
    id: string,
    next: { action: "deny" | "modify"; text?: string; title?: string } | null,
  ) => void;
}): JSX.Element {
  return (
    <div className="mb-3">
      <h5 className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
        {label} ({items.length})
      </h5>
      <ul className="space-y-1">
        {items.slice(0, 100).map((r) => (
          <DiffRowItem
            key={`${kind}-${r.id}`}
            kind={kind}
            row={r}
            decision={decisions[decisionKey(r.source_table, r.id)]}
            setRowDecision={setRowDecision}
          />
        ))}
        {items.length > 100 && (
          <li className="pt-1 text-xs text-slate-500">
            …and {items.length - 100} more {label.toLowerCase()} section
            {items.length - 100 === 1 ? "" : "s"}.
          </li>
        )}
      </ul>
    </div>
  );
}

function DiffRowItem({
  kind,
  row,
  decision,
  setRowDecision,
}: {
  kind: "added" | "changed" | "removed";
  row: DiffRow;
  decision: DecisionMap[string] | undefined;
  setRowDecision: (
    source_table: string,
    id: string,
    next: { action: "deny" | "modify"; text?: string; title?: string } | null,
  ) => void;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  // Edited text + title default to the parsed values when the operator
  // opens the editor. Submitting Save commits them to the decision map.
  const [draftTitle, setDraftTitle] = useState(row.title);
  const [draftText, setDraftText] = useState(row.text);

  const isDenied = decision?.action === "deny";
  const isModified = decision?.action === "modify";
  // Display the operator's modified text/title once committed.
  const displayTitle = isModified && decision?.title !== undefined ? decision.title : row.title;
  const displayText = isModified && decision?.text !== undefined ? decision.text : row.text;
  const hasBody = displayText && displayText.length > 0;
  const canModify = kind !== "removed"; // removed rows have no body to edit

  const accentBorder = isDenied
    ? "border-l-slate-300"
    : isModified
      ? "border-l-blue-400"
      : kind === "added"
        ? "border-l-emerald-400"
        : kind === "changed"
          ? "border-l-amber-400"
          : "border-l-red-400";

  const onSaveEdit = () => {
    setRowDecision(row.source_table, row.id, {
      action: "modify",
      text: draftText,
      title: draftTitle,
    });
    setEditing(false);
    setOpen(true);
  };

  const onCancelEdit = () => {
    setDraftTitle(displayTitle);
    setDraftText(displayText);
    setEditing(false);
  };

  return (
    <li
      className={`rounded border border-slate-200 border-l-4 bg-white px-3 py-2 ${accentBorder} ${
        isDenied ? "opacity-60" : ""
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div
              className={`font-mono text-xs font-semibold ${
                isDenied ? "text-slate-500 line-through" : "text-slate-900"
              }`}
            >
              {row.citation}
            </div>
            {isDenied && <span className="badge-gray">denied</span>}
            {isModified && <span className="badge-blue">edited</span>}
          </div>
          {displayTitle && (
            <div
              className={`mt-0.5 text-sm ${
                isDenied ? "text-slate-500 line-through" : "text-slate-700"
              }`}
            >
              {displayTitle}
            </div>
          )}
          {!displayTitle && kind === "removed" && (
            <div className="mt-0.5 text-sm italic text-slate-500">
              (section will be removed; text not shown — it's no longer in the
              uploaded file)
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2 text-xs">
          {hasBody && !editing && (
            <button
              type="button"
              className="text-brand-700 hover:underline"
              onClick={() => setOpen((s) => !s)}
            >
              {open ? "Hide text" : "Show text"}
            </button>
          )}
          {canModify && !editing && (
            <button
              type="button"
              className="text-brand-700 hover:underline"
              onClick={() => {
                setDraftTitle(displayTitle);
                setDraftText(displayText);
                setEditing(true);
                setOpen(true);
              }}
            >
              Edit
            </button>
          )}
          {!isDenied ? (
            <button
              type="button"
              className="text-red-700 hover:underline"
              onClick={() =>
                setRowDecision(row.source_table, row.id, { action: "deny" })
              }
              title="Skip this row when Apply runs"
            >
              Deny
            </button>
          ) : (
            <button
              type="button"
              className="text-emerald-700 hover:underline"
              onClick={() => setRowDecision(row.source_table, row.id, null)}
            >
              Approve
            </button>
          )}
          {isModified && !editing && (
            <button
              type="button"
              className="text-slate-500 hover:underline"
              onClick={() => setRowDecision(row.source_table, row.id, null)}
              title="Discard edits and approve as parsed"
            >
              Reset
            </button>
          )}
        </div>
      </div>

      {editing && (
        <div className="mt-2 space-y-2 rounded border border-blue-200 bg-blue-50 p-3">
          <div>
            <label className="block text-xs font-medium text-slate-700">
              Title
            </label>
            <input
              type="text"
              className="input mt-1 w-full text-sm"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700">
              Body text
            </label>
            <textarea
              className="input mt-1 h-44 w-full font-mono text-xs leading-relaxed"
              value={draftText}
              onChange={(e) => setDraftText(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-secondary" onClick={onCancelEdit}>
              Cancel
            </button>
            <button type="button" className="btn-primary" onClick={onSaveEdit}>
              Save edits
            </button>
          </div>
        </div>
      )}

      {open && hasBody && !editing && (
        <p
          className={`mt-2 whitespace-pre-wrap rounded bg-slate-50 p-2 text-xs leading-relaxed ${
            isDenied ? "text-slate-400 line-through" : "text-slate-700"
          }`}
        >
          {displayText.length > 1200
            ? `${displayText.slice(0, 1200)}…`
            : displayText}
        </p>
      )}
    </li>
  );
}

function StatusBadge({ status }: { status: string }): JSX.Element {
  const cls =
    status === "up_to_date"
      ? "bg-emerald-100 text-emerald-800"
      : status === "update_available"
        ? "bg-amber-100 text-amber-800"
        : status === "pulling"
          ? "bg-blue-100 text-blue-800"
          : "bg-red-100 text-red-800";
  return (
    <span className={`rounded px-2 py-1 text-xs font-medium ${cls}`}>{status}</span>
  );
}

function formatTs(ts: string | null): string {
  if (!ts) return "—";
  return new Date(ts).toLocaleString();
}

// ----- Admin Management card (system_admin only) ----------------------------
// REMOVED WHEN SSO LANDS — per SPEC §7 Q12 the SSO IdP becomes the source of
// truth for system_admin group/role; this UI gets deleted in that MR.

function AdminManagementCard(): JSX.Element {
  const myEmail = useAuthStore((s) => s.email);
  const [list, setList] = useState<SystemAdminEntry[] | null>(null);
  const [promoteEmail, setPromoteEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    try {
      setList((await admins.list()).admins);
    } catch (e) {
      setError((e as Error).message);
    }
  };
  useEffect(() => {
    void refresh();
  }, []);

  const onPromote = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!promoteEmail.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await admins.promote(promoteEmail.trim());
      setPromoteEmail("");
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const onDemote = async (email: string) => {
    const isSelf = email === myEmail;
    const last = list && list.length <= 1;
    if (last) {
      alert("Cannot demote the only remaining system_admin. Promote someone else first.");
      return;
    }
    const msg = isSelf
      ? `Demote yourself (${email})? You'll lose system_admin access immediately.`
      : `Demote ${email} to account_holder?`;
    if (!confirm(msg)) return;
    setBusy(true);
    setError(null);
    try {
      await admins.demote(email);
      await refresh();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card p-6">
      <h2 className="mb-1 text-lg font-medium text-slate-800">Admin Management</h2>
      <p className="mb-4 text-xs text-slate-500">
        System administrators can mutate the shared statute corpus. This page is removed when
        SSO replaces own-auth — see SPEC §7 Q12.
      </p>

      <form onSubmit={onPromote} className="mb-4 flex flex-wrap items-end gap-2">
        <div className="flex-1 min-w-[260px]">
          <label className="block text-sm font-medium text-slate-700">
            Promote a user by email
          </label>
          <input
            type="email"
            className="input mt-1 w-full"
            placeholder="user@example.com"
            value={promoteEmail}
            onChange={(e) => setPromoteEmail(e.target.value)}
          />
        </div>
        <button type="submit" className="btn-primary" disabled={busy || !promoteEmail.trim()}>
          Promote
        </button>
      </form>

      <h3 className="mb-2 text-sm font-medium text-slate-700">
        Current system administrators
      </h3>
      {list === null ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : list.length === 0 ? (
        <p className="text-sm text-slate-500">No system administrators yet.</p>
      ) : (
        <ul className="divide-y divide-slate-200">
          {list.map((a) => (
            <li key={a.user_id} className="flex items-center justify-between py-2 text-sm">
              <div>
                <span className="font-medium text-slate-900">{a.email}</span>
                <span className="ml-2 text-xs text-slate-500">
                  tenant #{a.tenant_id} · since {new Date(a.created_at).toLocaleDateString()}
                </span>
              </div>
              <button
                type="button"
                className="btn-secondary text-xs"
                onClick={() => onDemote(a.email)}
                disabled={busy || (list && list.length <= 1)}
              >
                Demote
              </button>
            </li>
          ))}
        </ul>
      )}
      {error && (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </section>
  );
}
